import React from 'react';
import T from 'i18n-react';
import clsx from 'clsx';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import HelpIcon from '@material-ui/icons/Help';
import icons from './Icons';
import ResponsiveTooltip from '../../Shared/ResponsiveTooltip';

export type AmenityProps = {
  id: number,
  name: string,
  key: string,
  description: string,
  amenityPlanId: number,
  checked: boolean,
};

const useStyles = makeStyles((theme) => ({
  description: {
    fontSize: theme.typography.pxToRem(14),
    padding: theme.spacing(1, 0),
  },
  itemContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing(1, 2, 1, 0),
  },
  iconContainer: {
    marginRight: theme.spacing(1),
  },
  item: {
    fontSize: theme.typography.pxToRem(14),
  },
  unavailable: {
    textDecoration: 'line-through',
    color: theme.palette.text.secondary,
  },
  itemDescription: {
    fontSize: theme.typography.pxToRem(14),
  },
  remarkContainer: {
    marginTop: theme.spacing(2),
  },
  remark: {
    fontSize: theme.typography.pxToRem(14),
  },
  tooltip: {
    color: theme.props.palette.primary.main,
    marginLeft: theme.spacing(1),
  },
}));

const Amenities = ({
  amenity,
  amenities = [],
  isAmenityUnavailable,
}: {
  amenity: string,
  amenities: Array<AmenityProps>,
  isAmenityUnavailable: boolean,
}) => {
  const classes = useStyles();

  const isChecked = (checked, amenityPlanId) =>
    !isAmenityUnavailable && (checked || !!amenityPlanId);
  const isListShown = () =>
    amenities.filter(({ checked, amenityPlanId }: AmenityProps) =>
      isChecked(checked, amenityPlanId),
    ).length !== 0 || isAmenityUnavailable;

  return (
    <Grid container>
      {isListShown() && (
        <>
          <Grid item>
            <Typography className={classes.description}>
              {T.translate('plan.amenities.description')}
            </Typography>
          </Grid>
          {amenities.map(
            (
              { name, key, description, checked, amenityPlanId }: AmenityProps,
              index,
            ) => (
              <React.Fragment key={index}>
                <Grid item xs={6} className={classes.itemContainer}>
                  <div className={classes.iconContainer}>{icons[key]}</div>
                  <Typography
                    className={clsx(
                      classes.item,
                      !isChecked(checked, amenityPlanId) && classes.unavailable,
                    )}
                  >
                    {name}
                  </Typography>
                  {isChecked(checked, amenityPlanId) && !!description && (
                    <ResponsiveTooltip
                      title={description}
                      icon
                      contentNode={<HelpIcon fontSize="small" />}
                      tooltipProps={{ className: classes.tooltip }}
                    />
                  )}
                </Grid>
              </React.Fragment>
            ),
          )}
        </>
      )}
      <Grid
        item
        xs={12}
        className={isListShown() ? classes.remarkContainer : null}
      >
        <Typography className={classes.remark}>{amenity}</Typography>
      </Grid>
    </Grid>
  );
};

export default Amenities;
